import { useIntl } from 'react-intl'
import { getDayNameAndDate, getDayNameAndDateWithTime } from 'helpers/dates'
import { BusTicketWithDetails } from 'store/api-slice/types'
import { useTransport } from 'store/transport-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { directionNames } from 'routes/Upsell/Bus/data'
import { useAutoScroll } from 'utils/hooks'
import { useEffect, useState } from 'react'
import RouteTitle from 'components/shared/RouteTitle'
import OrderedTicket, {
    OrderedTicketItem,
} from 'components/shared/OrderedTicket'
import { DEFAULT_QUANTITY } from 'store/transport-slice'

interface OrderedRouteProps {
    id: string
    routeIndex: number
    unitPrice: number
    ticketsQuantity: number
    editHandler: (routeIndex: number) => void
}

const OrderedRoute = ({
    id,
    routeIndex,
    unitPrice,
    ticketsQuantity,
    editHandler,
}: OrderedRouteProps) => {
    const { formatMessage } = useIntl()
    const { animatePendingScroll, calculateScroll, scrollIndex } =
        useAutoScroll()
    const { removeRouteFromCart } = useTransport(routeIndex)
    const { routes } = useAppSelector((state) => state.transport)
    const { ticket } = routes[routeIndex] || {}
    const [isDeleteError, setIsDeleteError] = useState(false)

    let separatedTickets: BusTicketWithDetails[] = []

    useEffect(() => {
        animatePendingScroll(`transport-ticket-${routeIndex}`)
    }, [scrollIndex])

    switch (ticket.detail.routeType) {
        case 'to_from_park':
            separatedTickets = [ticket, ticket]
            break

        case 'to_park':
        case 'from_park':
            separatedTickets = [ticket]
            break

        default:
            break
    }

    const handleDeleteRoute = () => {
        setIsDeleteError(false)
        removeRouteFromCart().then(({ isSuccess }) => {
            isSuccess
                ? calculateScroll({
                      trigerringSection: 'transport',
                      trigerringAction: 'delete',
                  })
                : setIsDeleteError(true)
        })
    }

    const handleEditRoute = () => {
        editHandler(routeIndex)
        calculateScroll({
            trigerringSection: 'transport',
            trigerringAction: 'edit',
            index: routeIndex,
        })
    }

    const routeItems: OrderedTicketItem[] = separatedTickets.map(
        (ticket, index) => {
            const {
                quantity,
                detail: {
                    routeType,
                    departurePlace,
                    date,
                    time,
                    returnDate,
                    returnTime,
                },
            } = ticket

            const isToPark =
                routeType === 'to_park' ||
                (routeType === 'to_from_park' && index === 0)
            const isFromPark =
                routeType === 'from_park' ||
                (routeType === 'to_from_park' && index === 1)

            const currentDate = isToPark ? date : returnDate || date
            const currentTime = isToPark ? time : returnTime || time

            return {
                quantity: quantity || DEFAULT_QUANTITY,
                text: (
                    <RouteTitle
                        from={formatMessage(
                            directionNames[
                                isToPark ? departurePlace : 'suntago'
                            ]
                        )}
                        to={formatMessage(
                            directionNames[
                                isFromPark ? departurePlace : 'suntago'
                            ]
                        )}
                        hasSmallDots
                        variant="bodySmall"
                        color="grayscale_900"
                    />
                ),
                date: currentTime
                    ? getDayNameAndDateWithTime({
                          date: currentDate,
                          time: currentTime,
                      })
                    : getDayNameAndDate({ date: currentDate }),
            }
        }
    )

    return (
        <OrderedTicket
            id={id}
            items={routeItems}
            price={unitPrice * ticketsQuantity}
            onDelete={handleDeleteRoute}
            onEdit={handleEditRoute}
            isActionError={isDeleteError}
            testIdPrefix={id.replace('-ticket', '')}
        />
    )
}

export default OrderedRoute
